import Ticket from '@/components/order/Ticket';
import PersonalisationExplainer from '@/components/order/PersonalisationExplainer';
import OpenInAppCard from '@/components/order/OpenInAppCard';
import WalletCard from '@/components/order/WalletCard';
import { OrderApi, TicketStatus, TicketType, GroupedTicket as GroupedTicketType, OrderInvoice } from '@/lib/OrderApi'
import type { Order } from '@/lib/OrderApi'
import type { RefundStatus } from '@/lib/OrderApi'
import { Await, createFileRoute, defer } from '@tanstack/react-router'
import OrderPageShell from '@/components/order/OrderPageShell';
import { ImportInfo } from '@/components/order/ImportInfo';
import { Fragment, useEffect, useCallback, useState, useRef } from 'react';
import { StyleOverwrites } from '@/providers/StyleOverwrites';
import GroupedTicketComponent from '@/components/order/GroupedTicket';
import Echo from 'laravel-echo';
import { broadcaster } from 'laravel-echo-api-gateway';
import { useLanguage } from '@/providers/LanguageProvider';
import { Dialog, DialogTrigger } from '@/components/shared/Dialog';
import GenerateInvoiceModal from '@/components/order/GenerateInvoiceModal';
import PayoutBankDetailsCard from '@/components/order/PayoutBankDetailsCard';
import { MainButton } from '@/components/shared/MainButton';
import { RiFileTextLine, RiDownloadLine, RiBankLine, RiRefund2Line, RiInformationLine, RiAlertLine, RiErrorWarningLine } from '@remixicon/react';
import { Money } from '@/lib/money';
import * as m from "@/paraglide/messages.js"

/**
 * FAS-1298 — an organizer's document is named by the organizer, and a filename
 * has no spaces: on a phone the browser breaks it wherever the line runs out
 * ("Plattegrond_Appelpop2026_VIP.p / df"). Offering a break opportunity after
 * each separator the organizer already typed makes it break where a reader
 * expects. `break-words` on the element still catches a single chunk that is
 * too long on its own.
 *
 * Split with a capture group rather than a lookbehind on purpose: older iOS
 * Safari fails to PARSE a lookbehind, which takes the whole bundle down instead
 * of just this line.
 */
const withBreakOpportunities = (label: string) =>
  label.split(/([_-])/).map((part, index) =>
    part === '_' || part === '-'
      ? <Fragment key={index}>{part}<wbr /></Fragment>
      : part
  );

function refundStatusText(status: RefundStatus, completedAt: string | null, language: string): string {
  switch (status) {
    case 'completed':
      return m.order_refund_completed_on({
        date: completedAt ? new Date(completedAt).toLocaleDateString(language) : '',
      });
    case 'failed':
      return m.order_refund_failed();
    default:
      return m.order_refund_on_its_way();
  }
}

// Loading skeleton component for tickets
const TicketSkeleton = () => (
  <div className="animate-pulse bg-[var(--shop-panelBackgroundColor)] rounded-xl p-4 mb-4">
    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      <div className="h-4 bg-gray-200 rounded w-1/3"></div>
    </div>
  </div>
);

// Loading state component
const LoadingState = () => (
  <div className='bg-gradient-to-b from-[#acb0ac] to-[#777877] sm:p-10 min-h-screen font-sans'>
    <div className='max-w-[640px] mx-auto'>
      <div className='bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] rounded-md rounded-bl-md rounded-br-md'>
        <div className='text-center text-lg font-bold py-4'>
          {m.order_loading()}
        </div>
      </div>
      <div className='px-4 sm:px-0 pb-40 space-y-4 mt-4'>
        <TicketSkeleton />
        <TicketSkeleton />
        <TicketSkeleton />
      </div>
    </div>
  </div>
);

const OrderNotFound = () => (
  <div className='bg-gradient-to-b from-[#9bb7ff] to-[#a7dbc7] sm:p-10 min-h-screen font-sans'>
    <div className='max-w-[640px] mx-auto'>
      <div className='bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] rounded-md rounded-bl-md rounded-br-md'>
        <div className='text-center text-lg font-bold pt-4'>{m.order_not_found()}</div>
        <div className='text-center text-sm py-4 px-4'>
          {m.order_not_found_description()}
          <br /><br />
          <p className='font-bold'>{m.order_not_found_busy()}</p>
          <br />
          {m.order_not_found_contact()} <a href="mailto:ticketing@fastlane.events" className="text-[var(--shop-ctaButtonColor)]">ticketing@fastlane.events</a>
        </div>
      </div>
    </div>
  </div>
)

export const loadOrder = async (orderId: string, language?: string) => {
  const ticketApi = new OrderApi(import.meta.env.VITE_API_URL);
  let order;
  let attempts = 0;
  const maxAttempts = 30;
  const interval = 1000; // 1 second

  while (attempts < maxAttempts) {
    try {
      order = await ticketApi.getOrder(orderId, language);
      break; // If successful, exit the loop
    } catch (error) {
      attempts++;
      if (attempts >= maxAttempts) {
        console.error(error);
        return null;
      }
      await new Promise(resolve => setTimeout(resolve, interval));
    }
  }

  return order;
}

type OrderLoaderData = {
  order: Promise<Order | null | undefined>
}

type OrderParams = {
  orderId: string
}

type OrderSearch = {
  invoiceSequenceId: string | undefined;
  openTicket?: string | undefined;
}

export const Route = createFileRoute('/order/$orderId')({
  validateSearch: (search: Record<string, unknown>): OrderSearch => {
    return {
      invoiceSequenceId: search.invoiceSequenceId as string | undefined,
      openTicket: search.openTicket as string | undefined,
    };
  },
  loader: async ({ params }: { params: OrderParams }): Promise<OrderLoaderData> => {
    return { order: defer(loadOrder(params.orderId)) }
  },
  component: Order,
})

// const getWarnings = (order: Order) => {
//   let warnings = [];
//   if (order.tickets.some(ticket => ticket.ticketStatus === TicketStatus.claimable)) {
//     warnings.push(<div>You have claimable tickets in your order. Please claim them before you can resell them.</div>);
//   }
//   return warnings;
// }

function getImportantInfo(data: Order) {
  const importantInfo = [];
  if (data.tickets.some(ticket =>
    ticket.ticketStatus === TicketStatus.available &&
    ticket.ticketType === TicketType.EntranceTicket &&
    'sealed' in ticket && ticket.sealed
  )) {
    importantInfo.push(m.order_tickets_sealed());
  }

  if (data.tickets.some(ticket => ticket.ticketStatus === TicketStatus.claimable)) {
    importantInfo.push(m.order_tickets_need_share());
  }

  if (data.tickets.some(ticket => 'canResell' in ticket && ticket.canResell)) {
    importantInfo.push(m.order_tickets_can_resell());

    // Find first resellable ticket to get the max percentage
    const resellableTicket = data.tickets.find(ticket =>
      'canResell' in ticket &&
      ticket.canResell &&
      'maxResellPercentage' in ticket
    );

    if (resellableTicket && 'maxResellPercentage' in resellableTicket) {
      importantInfo.push(m.order_tickets_resell_limit({ maxPercentage: resellableTicket.maxResellPercentage }));
    }
  }

  return importantInfo;
}

function Order() {
  const { order } = Route.useLoaderData() as OrderLoaderData
  const { orderId } = Route.useParams() as OrderParams
  const { invoiceSequenceId, openTicket } = Route.useSearch() as OrderSearch
  const [orderData, setOrderData] = useState<Order | null>(null)
  const [localInvoices, setLocalInvoices] = useState<OrderInvoice[]>([])
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false)
  const echoChannelRef = useRef<any>(null);
  const [isChannelReady, setIsChannelReady] = useState(false);
  const { currentLanguage } = useLanguage();

  useEffect(() => {
    order.then((data: Order | null | undefined) => {
      if (data) {
        setOrderData(data)
        setLocalInvoices(data.invoices || [])
      }
    })
  }, [order])

  const reloadOrder = useCallback(async () => {
    const newOrder = await loadOrder(orderId, currentLanguage)
    if (newOrder) {
      setOrderData(newOrder)
      setLocalInvoices(newOrder.invoices || [])
    }
  }, [orderId, currentLanguage]);

  // Check for sealed tickets with passed availability dates
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const checkSealedTickets = (data: Order) => {
      const hasAnySealed = data.tickets.some(ticket =>
        ticket.ticketStatus === TicketStatus.available &&
        'sealed' in ticket && ticket.sealed &&
        ticket.ticketType === TicketType.EntranceTicket
      );

      // Clear existing interval if no more sealed tickets
      if (!hasAnySealed && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
        return;
      }

      // Only set up new interval if we don't have one already
      if (hasAnySealed && !intervalId) {
        intervalId = setInterval(() => {
          const now = new Date();
          const hasPassedSealedTicket = data.tickets.some(ticket =>
            ticket.ticketStatus === TicketStatus.available &&
            ticket.ticketType === TicketType.EntranceTicket &&
            'sealed' in ticket && ticket.sealed &&
            'availableFrom' in ticket && ticket.availableFrom &&
            new Date(ticket.availableFrom.split('[')[0]) <= now
          );

          if (hasPassedSealedTicket) {
            reloadOrder();
          }
        }, 2000);
      }
    };

    if (orderData) {
      checkSealedTickets(orderData);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [orderData, reloadOrder]);

  // WebSocket connection useEffect
  useEffect(() => {
    if (!orderId) return;

    // Ensure the WebSocket host is defined in your environment variables
    const websocketHost = import.meta.env.VITE_WEBSOCKET_HOST;
    if (!websocketHost) {
      console.error("VITE_WEBSOCKET_HOST is not defined in environment variables.");
      setIsChannelReady(false);
      return;
    }

    const echoInstance = new Echo({
      broadcaster,
      host: websocketHost,
      debug: true,
    });

    // FAS-2023 — a reissued order's link id is `{ULID}-{key}`; the API broadcasts on the ULID.
    const channelName = `orders.${orderId.replace(/-.*$/, '')}`;
    const currentChannel = echoInstance.channel(channelName);

    currentChannel.listen('.OrderChanged', () => {
      console.warn(`OrderChanged event received for order ${orderId}, reloading.`);
      reloadOrder();
    });

    echoChannelRef.current = currentChannel;
    setIsChannelReady(true);
    console.log(`Echo channel '${channelName}' is now ready.`);

    return () => {
      console.log(`Leaving Echo channel: ${channelName}`);
      echoInstance.leave(channelName);
      echoChannelRef.current = null;
      setIsChannelReady(false);
    };
  }, [orderId, reloadOrder]);

  const handleInvoiceGenerated = useCallback((_invoice: OrderInvoice) => {
    setIsInvoiceModalOpen(false)
    // Reload order to get the invoice from the server
    reloadOrder()
  }, [reloadOrder])

  // useEffect to send 'order-loaded' whisper when data is ready and channel is ready
  useEffect(() => {
    if (orderData && isChannelReady && echoChannelRef.current) {
      const eventName = 'order-loaded';
      const payload = {
        orderId: orderData.orderId,
        timestamp: new Date().toISOString(),
        message: 'Order data has been loaded/updated on the client.'
      };
      console.log(`Attempting to whisper event '${eventName}' on channel '${echoChannelRef.current.name}' with payload:`, payload);

      // Ensure whisper method exists before calling, though it should for EchoChannel
      if (typeof echoChannelRef.current.whisper === 'function') {
        echoChannelRef.current.whisper(eventName, payload);
        // Server will likely see this as 'client-order-loaded'
      } else {
        console.error("Whisper method not available on channel object:", echoChannelRef.current);
      }
    }
  }, [orderData, isChannelReady]);

  return (
    <Await promise={order} fallback={<LoadingState />}>
      {(data) => {
        const displayData = orderData || data;
        if (!displayData || displayData == null) return <OrderNotFound />;
        return (
          <StyleOverwrites shopStyle={displayData.shopStyle}>
            <OrderPageShell event={displayData.event} shopStyle={displayData.shopStyle}>
              <OpenInAppCard order={displayData} />
              {getImportantInfo(displayData).length > 0 && (
                <div className='bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] rounded-md mt-4'>
                  <ImportInfo className='px-8 py-4'>
                    <ImportInfo.Title>{m.order_important_info_title()}</ImportInfo.Title>
                    {getImportantInfo(displayData).map((item, index) => (
                      <ImportInfo.Item key={index}>{item}</ImportInfo.Item>
                    ))}
                  </ImportInfo>
                </div>
              )}
              
              {/* Order Messages */}
              {displayData.messages?.length > 0 && displayData.messages.map((msg) => (
                <div
                  key={msg.key}
                  className={`rounded-md mt-4 px-4 sm:px-8 py-3 flex items-start gap-3 text-sm ${
                    msg.severity === 'error'
                      ? 'bg-red-500 text-white'
                      : msg.severity === 'warning'
                        ? 'bg-amber-500 text-white'
                        : 'bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)]'
                  }`}
                >
                  {msg.severity === 'error' ? (
                    <RiErrorWarningLine className='w-5 h-5 flex-shrink-0 mt-0.5' />
                  ) : msg.severity === 'warning' ? (
                    <RiAlertLine className='w-5 h-5 flex-shrink-0 mt-0.5' />
                  ) : (
                    <RiInformationLine className='w-5 h-5 flex-shrink-0 mt-0.5' />
                  )}
                  <p>{msg.description}</p>
                </div>
              ))}

              {/* Invoice Section */}
              {(invoiceSequenceId || localInvoices.length > 0) && (
                <div className='bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] rounded-md mt-4 px-4 sm:px-8 py-4'>
                  <h3 className='text-lg font-semibold mb-3 flex items-center gap-2'>
                    <RiFileTextLine className='w-5 h-5' />
                    {m.order_invoices_title()}
                  </h3>
                  
                  {/* Show existing invoices */}
                  {localInvoices.length > 0 && (
                    <div className='space-y-2 mb-4'>
                      {localInvoices.map((invoice, index) => (
                        <div key={index} className='flex items-center justify-between p-3 rounded-md bg-[var(--shop-panelBackgroundColor)] ring-1 ring-[var(--shop-borderColor)]'>
                          <span className='font-medium'>{invoice.invoice_number}</span>
                          <a
                            href={invoice.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className='flex items-center gap-1 text-[var(--shop-ctaButtonColor)] hover:opacity-80 transition-opacity'
                          >
                            <RiDownloadLine className='w-4 h-4' />
                            {m.order_invoice_download()}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Show generate invoice button only when invoiceSequenceId is present and no invoices exist */}
                  {invoiceSequenceId && localInvoices.length === 0 && (
                    <Dialog open={isInvoiceModalOpen} onOpenChange={setIsInvoiceModalOpen}>
                      <DialogTrigger asChild>
                        <MainButton className='w-full'>
                          <div className='flex items-center justify-center gap-2'>
                            {m.order_invoice_generate()}
                            <RiFileTextLine className='w-5 h-5' />
                          </div>
                        </MainButton>
                      </DialogTrigger>
                      <GenerateInvoiceModal
                        orderId={displayData.orderId}
                        invoiceSequenceId={invoiceSequenceId}
                        defaultName={`${displayData.contactDetails.firstName} ${displayData.contactDetails.lastName}`.trim()}
                        onSuccess={handleInvoiceGenerated}
                      />
                    </Dialog>
                  )}
                </div>
              )}

              <div className='px-4 sm:px-0 pb-40 space-y-4 mt-4'>
                {displayData.tickets.map((ticket) => {
                  if ('ticketType' in ticket && ticket.ticketType === TicketType.GroupedTicket) {
                    // Assert the type here since we've checked ticketType
                    const groupedTicket = ticket as GroupedTicketType;
                    return (
                      <GroupedTicketComponent
                        key={groupedTicket.ticketId}
                        orderId={displayData.orderId}
                        ticket={groupedTicket} // Use the asserted type
                        answers={displayData.formAnswers}
                        triggeredReload={reloadOrder}
                        marketplaceUrl={displayData.marketplaceUrl}
                        eventName={displayData.event.name}
                      />
                    );
                  }
                   else {
                    return (
                      <Ticket
                        orderId={displayData.orderId}
                        ticket={ticket}
                        key={ticket.ticketId}
                        answers={displayData.formAnswers}
                        triggeredReload={reloadOrder}
                        marketplaceUrl={displayData.marketplaceUrl}
                        eventName={displayData.event.name}
                        autoOpenAmend={openTicket === ticket.ticketId}
                        refunds={displayData.refunds}
                      />
                    );
                  }
                })}

                {/* FAS-974 — how personalisation, sealed tickets and the marketplace work. */}
                <PersonalisationExplainer order={displayData} />

                {/* Confirmation blocks the organizer configured (texts + documents, filtered on the ticket types in this order) */}
                {(displayData.confirmationBlocks?.length ?? 0) > 0 && (
                  <div className='bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] rounded-md px-4 sm:px-8 py-4 space-y-3'>
                    {displayData.confirmationBlocks!.map((block, index) => (
                      block.type === 'text' ? (
                        <div
                          key={index}
                          className='text-sm [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_a]:text-[var(--shop-ctaButtonColor)] [&_a]:underline [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mb-1 [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--shop-borderColor)] [&_blockquote]:pl-3 [&_hr]:my-3 [&_hr]:border-[var(--shop-borderColor)]'
                          // Authored by the organizer in the backoffice rich-text editor;
                          // the API sanitizes it through a tag allowlist before it gets here.
                          dangerouslySetInnerHTML={{ __html: block.html }}
                        />
                      ) : (
                        // FAS-1298 — the organizer names the file, so the label can be far wider
                        // than a phone. The row wraps and the filename breaks instead of shoving
                        // the download link outside the card: min-w-0 lets the label shrink below
                        // its min-content width, which is what break-words needs to take effect.
                        <div key={index} className='flex flex-wrap items-center justify-between gap-x-3 gap-y-2 p-3 rounded-md bg-[var(--shop-panelBackgroundColor)] ring-1 ring-[var(--shop-borderColor)]'>
                          <span className='font-medium flex items-start gap-2 min-w-0'>
                            <RiFileTextLine className='w-4 h-4 flex-shrink-0 mt-0.5' />
                            <span className='min-w-0 break-words'>{withBreakOpportunities(block.label)}</span>
                          </span>
                          <a
                            href={block.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className='flex items-center gap-1 flex-shrink-0 text-[var(--shop-ctaButtonColor)] hover:opacity-80 transition-opacity'
                          >
                            <RiDownloadLine className='w-4 h-4' />
                            {m.order_event_info_download()}
                          </a>
                        </div>
                      )
                    ))}
                  </div>
                )}

                {/* Package deal wallets */}
                {displayData.wallets && Object.values(displayData.wallets).map((wallet) => (
                  <WalletCard key={wallet.walletId} wallet={wallet} />
                ))}

                {/* Manual payout bank details (seller owed money with no payment to refund against) */}
                {(displayData.requiresPayoutBankDetails || displayData.payoutBankDetails) && (
                  <PayoutBankDetailsCard
                    orderId={displayData.orderId}
                    requiresPayoutBankDetails={displayData.requiresPayoutBankDetails ?? false}
                    reason={displayData.payoutBankDetailsReason}
                    payoutBankDetails={displayData.payoutBankDetails ?? null}
                    onSuccess={reloadOrder}
                  />
                )}

                {/* Refunds Section (pay.nl refunds of this order with a tracked status) */}
                {(displayData.refunds?.length ?? 0) > 0 && (
                  <div className='bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] rounded-md px-4 sm:px-8 py-4'>
                    <h3 className='text-lg font-semibold mb-3 flex items-center gap-2'>
                      <RiRefund2Line className='w-5 h-5' />
                      {m.order_refunds_title()}
                    </h3>
                    <div className='space-y-3'>
                      {displayData.refunds!.map((refund) => (
                        <div
                          key={refund.refundId}
                          className='flex flex-col gap-1 p-3 rounded-md ring-1 ring-[var(--shop-borderColor)]'
                        >
                          <div className='flex items-center justify-between'>
                            <span className='font-medium'>
                              {refund.description ?? m.order_refund_default_description()}
                            </span>
                            <span>
                              {Money.fromInteger(refund.amount, refund.currency).display(currentLanguage)}
                            </span>
                          </div>
                          <span className='text-sm text-[var(--shop-textColor)]/80'>
                            {refundStatusText(refund.status, refund.completedAt, currentLanguage)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payouts Section (refunds from paid ticket share or marketplace sale) */}
                {(displayData.payouts?.length ?? 0) > 0 && (
                  <div className='bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] rounded-md px-4 sm:px-8 py-4'>
                  <h3 className='text-lg font-semibold mb-3 flex items-center gap-2'>
                    <RiBankLine className='w-5 h-5' />
                    {m.order_payouts_title()}
                  </h3>
                  {displayData.payouts!.some((payout) => !payout.failedRefund) && (
                    <p className='text-sm text-[var(--shop-textColor)]/90 mb-4'>
                      {m.order_payout_refund_info()}
                    </p>
                  )}
                  <div className='space-y-3'>
                    {displayData.payouts!.map((payout, index) => (
                      <div
                        key={index}
                        className='flex flex-col gap-1 p-3 rounded-md ring-1 ring-[var(--shop-borderColor)]'
                      >
                        <div className='flex items-center justify-between'>
                          <span className='font-medium'>{payout.description}</span>
                          <span>
                            {Money.fromInteger(payout.amount, payout.currency).display(currentLanguage)}
                          </span>
                        </div>
                        <span className='text-sm text-[var(--shop-textColor)]/80'>
                          {payout.status
                            ? refundStatusText(payout.status, payout.completedAt ?? null, currentLanguage)
                            : payout.failedRefund && !payout.processed
                              ? (displayData.payoutBankDetails
                                ? m.order_payout_refund_by_bank_pending()
                                : m.order_payout_refund_by_bank_needs_details())
                            : payout.processed
                            ? m.order_payout_processed()
                            : (displayData.requiresPayoutBankDetails || displayData.payoutBankDetails)
                              ? m.order_payout_pending_manual()
                              : m.order_payout_pending()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </OrderPageShell>
          </StyleOverwrites>
        );
      }}
    </Await>
  )
}
